// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Stage 4 of the Power Flow model: conductor paths.
 *
 * **Orthogonal, never curved.** This is the one place where the previous
 * graph's geometry could not be reused: `kgEdgeGeometry.ts` drew beziers between
 * group headers, and a bezier between a switchboard and a PDU reads as "sort of
 * connected". A one-line is drawn with straight runs and right angles, and an
 * operator reads conductor identity off exactly that.
 *
 * Three path shapes, and the choice between them is geometric rather than
 * configured:
 *
 * - **straight** — the two ports share an x within a hair's breadth. One
 *   vertical line.
 * - **elbow** — the usual case. Down out of the source, across at a mid-height
 *   lane, down into the target. Two right angles.
 * - **tap** — a bus at one end. The conductor leaves (or joins) the bar
 *   directly above the other end, so the run is a short vertical stub rather
 *   than a long diagonal reach. This is what makes a bus read as a bar that
 *   things hang off.
 *
 * Cycle-broken links — an A/B bus tie — run **sideways** between two nodes at
 * the same depth and get their own shape so the renderer can draw them
 * distinctly. They are not conductors down the drawing and must not pretend to
 * be.
 */

import type { PowerFlowLink } from './powerFlowGraph';
import { portOf, type PowerFlowLayout, type PowerFlowNode } from './powerFlowLayout';
import type { VoltageClass } from '../config/powerFlowSymbols';

export type PowerFlowRoute = 'straight' | 'elbow' | 'tap' | 'tie' | 'stack';

/** A point on a conductor, in stage coordinates. */
export interface ConductorAnchor {
  x: number;
  y: number;
}

export interface PowerFlowConductor {
  id: string;
  source: string;
  target: string;
  /**
   * The polyline, in stage coordinates, source end first. Every conductor is
   * axis-aligned M/L segments and this is the one authoritative geometry:
   * breaker placement, flow timing and the tests all read it, and `d` below is
   * only its serialisation for the `<path>`.
   */
  points: ConductorAnchor[];
  /** SVG path — `pathOf(points)`. */
  d: string;
  /**
   * Where a *series annotation* sits on this conductor, in stage coordinates.
   *
   * A one-line puts protection in series on the wire, not beside it: the
   * breaker between a switchboard and its UPS is drawn on the drop out of the
   * board, a little below the bar. Both references do exactly this, and
   * `power-flow-visual-language.md` Sheet 02 already states the rule — *every
   * protective device is an annotation on an edge, never a node*. Breakers are
   * placed by `breakerPlacement` (at the run's bus end, clear of the bus text);
   * this anchor is its last resort, and the conductor's own status mark uses it
   * for the same reason.
   *
   * Deliberately not the path's geometric midpoint. On an elbow the midpoint
   * can land on the horizontal crossing run, which is the one segment that is
   * an artefact of routing rather than a real piece of cable — an annotation
   * there reads as belonging to whatever the run passes over. The anchor always
   * sits on the vertical drop out of the source.
   */
  anchor: ConductorAnchor;
  route: PowerFlowRoute;
  /** Heavier for medium voltage; the drawing convention. */
  voltageClass: VoltageClass;
  /** True for a link removed to break a cycle — a bus tie. */
  isTie: boolean;
  relTypes: string[];
  /**
   * Breakers the payload placed on this run (`PowerFlowLink.via`), drawn as
   * one series annotation. Empty or absent: none published.
   */
  via?: string[];
  /**
   * Which end of the run is a bus, if either. A breaker belongs to the board
   * it was folded into, which is always this end, and a one-line draws it at
   * that end — below the bar on a feeder out, above it on an incomer.
   */
  busEnd?: 'source' | 'target';
  /**
   * Both ends are buses (a bypass, a wrap): the board at `target` holds a
   * breaker too, so placement may fall back to that end.
   */
  busToBus?: boolean;
}

/** The bus end of a run between these two nodes; the source wins a bus-to-bus run. */
const busEndOf = (source: PowerFlowNode, target: PowerFlowNode): PowerFlowConductor['busEnd'] => {
  if (source.shape === 'bus') return 'source';
  if (target.shape === 'bus') return 'target';
  return undefined;
};

/** Below this x difference the two ports count as vertically aligned. */
const ALIGNMENT_EPSILON = 1.5;

/** How far a conductor drops out of a node before turning. */
const STUB = 14;

/**
 * How far down the drop out of the source the series anchor sits, as a fraction
 * of that drop.
 *
 * Above the halfway point on purpose. Protection is drawn near the thing it
 * protects the rest of the circuit *from* — the board it is tapped off — and on
 * a tap out of a bus bar that reading is the whole point of the symbol's
 * position. It also keeps the annotation clear of the lane-spread crossing band
 * lower down, where several conductors run side by side.
 */
const ANCHOR_FRACTION = 0.35;

export interface RoutePowerFlowOptions {
  /**
   * Lane offset per conductor sharing a crossing band, so parallel runs at the
   * same depth do not land on top of each other. Zero disables lane spreading.
   */
  laneStep?: number;
  /** Cap on distinct lanes before offsets wrap. */
  maxLanes?: number;
}

/**
 * Turn links into paths.
 *
 * `treeLinks` become downward conductors; `cycleLinks` become ties. Both are
 * returned in one list, sorted by id, because the renderer draws them in a
 * single SVG layer.
 */
export const routePowerFlow = (
  layout: PowerFlowLayout,
  treeLinks: readonly PowerFlowLink[],
  cycleLinks: readonly PowerFlowLink[],
  options: RoutePowerFlowOptions = {}
): PowerFlowConductor[] => {
  const laneStep = options.laneStep ?? 6;
  const maxLanes = options.maxLanes ?? 8;

  // Conductors crossing the same gap between two ranks share a horizontal
  // band. Spread them across lanes in a stable order so two runs between the
  // same pair of ranks are distinguishable.
  const laneOf = new Map<string, number>();
  const bandCounts = new Map<string, number>();
  for (const link of treeLinks.slice().sort((a, b) => (a.id < b.id ? -1 : 1))) {
    const source = layout.nodesById.get(link.source);
    const target = layout.nodesById.get(link.target);
    if (!source || !target) continue;
    const band = `${source.rank}:${target.rank}`;
    const index = bandCounts.get(band) ?? 0;
    bandCounts.set(band, index + 1);
    laneOf.set(link.id, index % maxLanes);
  }

  const conductors: PowerFlowConductor[] = [];

  for (const link of treeLinks) {
    const source = layout.nodesById.get(link.source);
    const target = layout.nodesById.get(link.target);
    // A link whose endpoint is not laid out cannot be drawn. `powerFlowGraph`
    // has already counted the payload-level version of this as a dangling
    // link, so nothing is being silently swallowed here.
    if (!source || !target) continue;

    conductors.push(
      buildConductor(link, source, target, laneOf.get(link.id) ?? 0, laneStep, false)
    );
  }

  for (const link of cycleLinks) {
    const source = layout.nodesById.get(link.source);
    const target = layout.nodesById.get(link.target);
    if (!source || !target) continue;
    conductors.push(buildTie(link, source, target));
  }

  return conductors.sort((a, b) => (a.id < b.id ? -1 : 1));
};

/** The fields every conductor between these two nodes shares, geometry aside. */
const conductorBase = (
  link: PowerFlowLink,
  source: PowerFlowNode,
  target: PowerFlowNode,
  isTie: boolean
) => ({
  id: link.id,
  source: link.source,
  target: link.target,
  voltageClass: heavierOf(source, target),
  isTie,
  relTypes: link.relTypes,
  via: link.via,
  busEnd: busEndOf(source, target),
  ...(source.shape === 'bus' && target.shape === 'bus' ? { busToBus: true } : {}),
});

/** The polyline and its serialisation, rounded once so the two never disagree. */
const geometry = (points: readonly ConductorAnchor[]) => {
  const rounded = points.map(p => ({ x: round(p.x), y: round(p.y) }));
  return { points: rounded, d: pathOf(rounded) };
};

const buildConductor = (
  link: PowerFlowLink,
  source: PowerFlowNode,
  target: PowerFlowNode,
  lane: number,
  laneStep: number,
  isTie: boolean
): PowerFlowConductor => {
  // A bus leaves from directly above its child and joins from directly below
  // its parent — clamped to the bar, so a child outside the bar's span still
  // connects at the nearest end rather than off the end of it.
  const exitX =
    source.shape === 'bus'
      ? clamp(centreOf(target), source.x, source.x + source.w)
      : centreOf(source);
  const entryX =
    target.shape === 'bus'
      ? clamp(centreOf(source), target.x, target.x + target.w)
      : centreOf(target);

  const exitY = source.y + source.h;
  const entryY = target.y;

  const aligned = Math.abs(exitX - entryX) <= ALIGNMENT_EPSILON;
  const touchesBus = source.shape === 'bus' || target.shape === 'bus';

  const base = conductorBase(link, source, target, isTie);
  if (aligned) {
    return {
      ...base,
      ...geometry([
        { x: exitX, y: exitY },
        { x: entryX, y: entryY },
      ]),
      anchor: { x: round(exitX), y: round(exitY + (entryY - exitY) * ANCHOR_FRACTION) },
      route: touchesBus ? 'tap' : 'straight',
    };
  }

  // Cross at a lane inside the gap, biased toward the target so the horizontal
  // run sits closer to what it is feeding — which is how a one-line is drawn:
  // the branch happens near the load, not near the source.
  const gap = Math.max(entryY - exitY, STUB * 2);
  const crossY = exitY + gap * 0.55 + lane * laneStep;

  return {
    ...base,
    ...geometry([
      { x: exitX, y: exitY },
      { x: exitX, y: crossY },
      { x: entryX, y: crossY },
      { x: entryX, y: entryY },
    ]),
    // On the drop out of the source, never on the horizontal crossing run.
    anchor: { x: round(exitX), y: round(exitY + (crossY - exitY) * ANCHOR_FRACTION) },
    route: touchesBus ? 'tap' : 'elbow',
  };
};

/**
 * A tie runs sideways at the depth of the two nodes it joins, looping just
 * below them so it does not overdraw either box.
 *
 * Drawn deliberately unlike a conductor: it is the edge the ranking pass had to
 * remove to make the graph acyclic, and presenting it as a normal downward feed
 * would assert a direction the data does not support.
 */
const buildTie = (
  link: PowerFlowLink,
  source: PowerFlowNode,
  target: PowerFlowNode
): PowerFlowConductor => {
  const fromRight = centreOf(source) <= centreOf(target);
  const exitX = fromRight ? source.x + source.w : source.x;
  const entryX = fromRight ? target.x : target.x + target.w;
  const exitY = source.y + source.h / 2;
  const entryY = target.y + target.h / 2;
  const midX = (exitX + entryX) / 2;

  return {
    ...conductorBase(link, source, target, true),
    ...geometry([
      { x: exitX, y: exitY },
      { x: midX, y: exitY },
      { x: midX, y: entryY },
      { x: entryX, y: entryY },
    ]),
    // The one case where the middle of the run is right: a tie's annotation is
    // the tie breaker itself, and both references label it dead centre of the
    // span ("N.O. 600 A · loop tie"). On the usual same-rank tie this lands on
    // the horizontal run, which here is real conductor rather than routing.
    anchor: { x: round(midX), y: round((exitY + entryY) / 2) },
    route: 'tie',
  };
};

/**
 * A conductor to a child stacked vertically under its parent.
 *
 * The site's own Ignition sheets stack the RPPs under each PDU in a column and
 * feed them from one vertical trunk running down the left side, with a short
 * horizontal into each box. That is what this draws: out of the parent's
 * underside at `trunkX`, straight down to the child's mid-height, then right
 * into its left edge. A stack of four needs four of these sharing the trunk,
 * and they overdraw each other along it, which is exactly the picture.
 */
export const buildStackConductor = (
  link: PowerFlowLink,
  parent: PowerFlowNode,
  child: PowerFlowNode,
  trunkX: number
): PowerFlowConductor => {
  const exitY = parent.y + parent.h;
  const entryY = child.y + child.h / 2;
  const entryX = child.x;
  return {
    ...conductorBase(link, parent, child, false),
    ...geometry([
      { x: trunkX, y: exitY },
      { x: trunkX, y: entryY },
      { x: entryX, y: entryY },
    ]),
    anchor: { x: round(trunkX), y: round(exitY + (entryY - exitY) * ANCHOR_FRACTION) },
    route: 'stack',
  };
};

/** What a bridge may not cross, and where it may run instead. */
export interface BridgeRouting {
  /**
   * Every node on the sheet. A bridge segment may pass through none of them
   * other than its own two ends.
   */
  obstacles: readonly PowerFlowNode[];
  /**
   * Free vertical lanes: the x of each gap between adjacent columns. A
   * bridge's long vertical run goes down one of these, never down a column.
   */
  gutters: readonly number[];
  /**
   * The sheet margins outside the first and last column, tried only when no
   * gutter gives a clear run: the left margin carries the band titles.
   */
  margins?: readonly number[];
  /**
   * Where the run turns horizontal toward the target: just above the target's
   * row for a same-band bridge, the middle of the band gap for a cross-band
   * one. Defaults to `crossY`.
   */
  targetLaneY?: number;
  /**
   * Where conductors already join each bar, keyed by bus id: `above` for an
   * incomer, `below` for a feeder. A bridge into a bar picks its own point
   * clear of these and records it, so the caller passes the same map to every
   * bridge. Without it two UPS outputs shared one drop into the output board,
   * and their breakers were pushed up out of the board to the UPS.
   */
  barTaps?: Map<string, BarTap[]>;
  /**
   * Bridges already routed down each gutter, keyed by gutter x. A bridge takes
   * the next lane and records it, so two bridges down one gutter never share a
   * run: from either side, they met end to end and read as one wire joining
   * two blocks.
   */
  gutterLanes?: Map<number, number>;
  /**
   * How far a bar may be extended past either end to give a drop its own
   * place, when no point along it has room. The caller widens the bar to
   * reach the drop (see `BAR_EXTENSION_OVERHANG`): a bypass then leaves its
   * board like every other feeder instead of detouring to a gutter.
   */
  barExtension?: number;
  /**
   * The x range a bar may be extended within, overhang included: its block's
   * frame. An extension that would cross it is not tried.
   */
  barLimits?: (barId: string) => { min: number; max: number } | undefined;
}

/** Where a conductor joins a bar, and from which side. */
export interface BarTap {
  x: number;
  side: 'above' | 'below';
  /** Recorded by a bridge, which is what sets the next bridge's turn. */
  bridge?: boolean;
}

/**
 * Spacing between two joins on the same side of a bar: a breaker's glyph and
 * its alarm mark (see `breakerBox`) must not overlap the next one's.
 */
const SAME_SIDE_TAP_GAP = 68;
/** Spacing from a join on the other side, so neither reads as running through. */
const OTHER_SIDE_TAP_GAP = 16;
/** Search step along a bar for a free join. */
const TAP_SEARCH_STEP = 4;
/** Height between the turns of bridges nested into one bar: 8 px reads as one line at fit zoom. */
const NEST_TURN_STEP = 16;
/** How far a bar runs past a drop it was extended to reach. */
export const BAR_EXTENSION_OVERHANG = 8;
/** Offset between bridges sharing a gutter, across and down. */
const GUTTER_LANE_STEP = 8;
/** Lanes either side of a gutter's centre; it is `SHEET_CHROME.columnGap` (32) wide. */
const MAX_GUTTER_LANE_OFFSET = 12;

/**
 * The point on `bar` nearest `preferred` that keeps clear of the joins already
 * on it, searching toward `toward` first. `preferred` when nothing is free.
 */
const freeBarX = (
  bar: PowerFlowNode,
  side: BarTap['side'],
  preferred: number,
  toward: number,
  taps: readonly BarTap[],
  sameSideGap = SAME_SIDE_TAP_GAP
): number => {
  const low = bar.x;
  const high = bar.x + bar.w;
  const start = clamp(preferred, low, high);
  const free = (x: number) =>
    taps.every(
      tap => Math.abs(tap.x - x) >= (tap.side === side ? sameSideGap : OTHER_SIDE_TAP_GAP)
    );
  const first = toward >= start ? 1 : -1;
  for (let offset = 0; offset <= high - low; offset += TAP_SEARCH_STEP) {
    for (const direction of [first, -first]) {
      const x = start + direction * offset;
      if (x >= low && x <= high && free(x)) return x;
      if (offset === 0) break;
    }
  }
  return preferred;
};

/**
 * A conductor between two block columns, or between the input band and the
 * output band.
 *
 * The site's Ignition screens draw these as off-sheet stubs because each block
 * is a separate screen there. Ours is one canvas, so the connection is drawn:
 * a UPS feeding the output board below, the input board's bypass to it, the
 * MV riser looping from one block's transformer to the next.
 *
 * **A bridge never passes through a node.** Its long vertical run goes down a
 * gutter — the gap between two columns, or the sheet margin — and it turns
 * horizontal into the target only at the target's row, or in the gap between
 * the bands. Without that, the input board's bypass to the output board
 * dropped straight through the mechanical switchgear and the MDP bars beneath
 * it, which on a one-line reads as a feed *to* them.
 *
 * Shapes, in order of preference; the first whose every segment is clear of
 * `routing.obstacles` (other than the two ends) is drawn:
 *
 * - Endpoints on the same row (the riser): a horizontal run from the source's
 *   near side to the target's near side, at mid-height. Blocked by a node
 *   between them, it loops just below the row instead.
 * - The direct drop: down out of the source to `crossY`, across, and down into
 *   the target. Kept when nothing is in the way — a UPS directly above the
 *   output board it feeds is the picture the site's sheets draw.
 * - The gutter route: a short stub down out of the source, across to the
 *   nearest gutter, down the gutter to the target lane, across to the target,
 *   and down into it. Gutters are tried nearest-first, then the margins.
 *
 * With no `routing` (the tests' bare geometry) the direct drop is drawn.
 */
export const buildBridgeConductor = (
  link: PowerFlowLink,
  source: PowerFlowNode,
  target: PowerFlowNode,
  crossY: number,
  isTie: boolean,
  routing?: BridgeRouting
): PowerFlowConductor => {
  const clear = (points: readonly ConductorAnchor[]): boolean =>
    !routing || polylineIsClear(points, routing.obstacles, [source.id, target.id]);
  // A drop from a free point on the bar is one the layout did not plan for,
  // so it keeps real clearance: running along another bar's end or beside a
  // load's drop reads as a feed to it.
  const clearWithMargin = (points: readonly ConductorAnchor[]): boolean =>
    !routing ||
    polylineIsClear(
      points,
      routing.obstacles.map(n => ({
        ...n,
        x: n.x - OTHER_SIDE_TAP_GAP,
        y: n.y - OTHER_SIDE_TAP_GAP,
        w: n.w + 2 * OTHER_SIDE_TAP_GAP,
        h: n.h + 2 * OTHER_SIDE_TAP_GAP,
      })),
      [source.id, target.id]
    );

  const base = conductorBase(link, source, target, isTie);

  const sameRow = Math.abs(source.y + source.h / 2 - (target.y + target.h / 2)) < source.h;
  if (sameRow) {
    const fromRight = centreOf(source) <= centreOf(target);
    const exitX = fromRight ? source.x + source.w : source.x;
    const entryX = fromRight ? target.x : target.x + target.w;
    const y = source.y + source.h / 2;
    const across: ConductorAnchor[] = [
      { x: exitX, y },
      { x: entryX, y },
    ];
    if (clear(across)) {
      return {
        ...base,
        ...geometry(across),
        anchor: { x: round((exitX + entryX) / 2), y: round(y) },
        route: 'straight',
      };
    }
    // Something sits between them on the row: loop underneath it.
    const loopY = Math.max(source.y + source.h, target.y + target.h) + STUB;
    const below: ConductorAnchor[] = [
      { x: centreOf(source), y: source.y + source.h },
      { x: centreOf(source), y: loopY },
      { x: centreOf(target), y: loopY },
      { x: centreOf(target), y: target.y + target.h },
    ];
    return {
      ...base,
      ...geometry(below),
      anchor: { x: round((centreOf(source) + centreOf(target)) / 2), y: round(loopY) },
      route: 'elbow',
    };
  }

  const tapsOn = (bar: PowerFlowNode) => routing?.barTaps?.get(bar.id) ?? [];
  const exitX = centreOf(source);
  // `extend`: a direct drop may land past the bar's end, within its frame,
  // and the caller extends the bar to meet it. An incomer from beyond the end
  // then drops straight onto it instead of turning in across the others.
  const entryOnBar = (preferred: number, extend = false) => {
    const limits = extend ? routing?.barLimits?.(target.id) : undefined;
    const low = limits ? Math.min(target.x, limits.min + BAR_EXTENSION_OVERHANG) : target.x;
    const high = limits
      ? Math.max(target.x + target.w, limits.max - BAR_EXTENSION_OVERHANG)
      : target.x + target.w;
    return freeBarX(
      { ...target, x: low, w: high - low },
      'above',
      clamp(preferred, low, high),
      centreOf(target),
      tapsOn(target)
    );
  };
  const exitY = source.y + source.h;
  const entryY = target.y;
  const route: PowerFlowRoute = target.shape === 'bus' ? 'tap' : 'elbow';
  const claim = (points: readonly ConductorAnchor[], gutterX?: number) => {
    const record = (bar: PowerFlowNode, tap: BarTap) => {
      if (bar.shape !== 'bus' || !routing?.barTaps) return;
      routing.barTaps.set(bar.id, [...tapsOn(bar), tap]);
    };
    record(source, { x: points[0].x, side: 'below', bridge: true });
    record(target, { x: points[points.length - 1].x, side: 'above', bridge: true });
    if (gutterX !== undefined && routing?.gutterLanes) {
      routing.gutterLanes.set(gutterX, (routing.gutterLanes.get(gutterX) ?? 0) + 1);
    }
  };

  // Bridges into one bar nest: the caller routes the farthest first, it takes
  // the outer landing and the lowest turn, and each next one turns a step
  // higher, so no two share a horizontal run.
  const nested =
    target.shape === 'bus'
      ? tapsOn(target).filter(tap => tap.bridge && tap.side === 'above').length * NEST_TURN_STEP
      : 0;
  const turnY = crossY - nested;
  const directFrom = (fromX: number): ConductorAnchor[] => {
    const toX = target.shape === 'bus' ? entryOnBar(fromX, true) : centreOf(target);
    if (Math.abs(toX - fromX) <= ALIGNMENT_EPSILON) {
      return [
        { x: fromX, y: exitY },
        { x: fromX, y: entryY },
      ];
    }
    return [
      { x: fromX, y: exitY },
      { x: fromX, y: turnY },
      { x: toX, y: turnY },
      { x: toX, y: entryY },
    ];
  };
  const direct = directFrom(exitX);
  // Out of a bar, the drop may leave anywhere along it that is free: the
  // middle is often blocked by the bar's own loads, and a gutter is the long
  // way round (a bypass ran out to the margin and back across the whole
  // column). Free points nearest the target are tried first.
  const barExits = (): number[] => {
    if (source.shape !== 'bus') return [];
    const taps = tapsOn(source);
    const exits: number[] = [];
    const along: number[] = [];
    for (let x = source.x; x < source.x + source.w; x += TAP_SEARCH_STEP) along.push(x);
    for (const x of [...along, source.x + source.w]) {
      if (freeBarX(source, 'below', x, x, taps) === x) exits.push(x);
    }
    const toward = centreOf(target);
    return exits.sort((a, b) => Math.abs(a - toward) - Math.abs(b - toward));
  };
  // Failing that, just past an end of the bar, which the caller extends: the
  // right end first, so every block's bypass sits on the same side, and only
  // within the block's frame.
  const pastEnds = (): number[] => {
    const reach = routing?.barExtension;
    if (source.shape !== 'bus' || !reach) return [];
    const limits = routing?.barLimits?.(source.id) ?? { min: -Infinity, max: Infinity };
    const right = source.x + source.w + reach;
    const left = source.x - reach;
    return [
      ...(right + BAR_EXTENSION_OVERHANG <= limits.max ? [right] : []),
      ...(left - BAR_EXTENSION_OVERHANG >= limits.min ? [left] : []),
    ];
  };
  const directPath = clear(direct)
    ? direct
    : [...barExits(), ...pastEnds()].map(directFrom).find(points => clearWithMargin(points));
  if (directPath) {
    claim(directPath);
    return {
      ...base,
      ...geometry(directPath),
      anchor: {
        x: round(directPath[0].x),
        y: round(exitY + (turnY - exitY) * ANCHOR_FRACTION),
      },
      route,
    };
  }

  // Down a gutter. The stub out of the source is `STUB` long, so the anchor —
  // the conductor's own status mark, and one day its breaker — still sits on
  // the drop out of the board it belongs to.
  const viaGutter = (gutter: number): ConductorAnchor[] => {
    // Its own lane: offset toward the side it comes from, and down by the
    // same step, so neither its vertical nor its stub meets another bridge's.
    const lane = routing?.gutterLanes?.get(gutter) ?? 0;
    // Out of a bar at the end facing the gutter, so the stub across to it is
    // short and runs past none of the bar's feeders. Its breaker goes on the
    // run down the gutter, not on the stub, so only the drop itself must
    // clear the feeders beside it.
    const leaveX =
      source.shape === 'bus'
        ? freeBarX(source, 'below', gutter, centreOf(source), tapsOn(source), OTHER_SIDE_TAP_GAP)
        : centreOf(source);
    const side = leaveX < gutter ? -1 : 1;
    const gutterX = gutter + side * Math.min(lane * GUTTER_LANE_STEP, MAX_GUTTER_LANE_OFFSET);
    const stubY = exitY + STUB + lane * GUTTER_LANE_STEP;
    const laneY = (routing?.targetLaneY ?? crossY) - nested + lane * GUTTER_LANE_STEP;
    // A bar is joined at the end nearest the gutter, so the lane into it is
    // as short as the drawing allows; a box is joined at its centre.
    const gutterEntryX = target.shape === 'bus' ? entryOnBar(gutterX) : centreOf(target);
    const entry: ConductorAnchor[] =
      Math.abs(gutterX - gutterEntryX) <= ALIGNMENT_EPSILON
        ? [{ x: gutterX, y: entryY }]
        : [
            { x: gutterX, y: laneY },
            { x: gutterEntryX, y: laneY },
            { x: gutterEntryX, y: entryY },
          ];
    return [{ x: leaveX, y: exitY }, { x: leaveX, y: stubY }, { x: gutterX, y: stubY }, ...entry];
  };
  // A gutter no other bridge uses first, a margin before a shared gutter:
  // two bridges side by side down one gap read as one, and a block's bypass
  // detouring into its neighbour's gap is the line nobody needs.
  const used = (gutterX: number) => (routing?.gutterLanes?.get(gutterX) ?? 0) > 0;
  const candidates = nearestFirst(routing?.gutters ?? [], exitX).concat(
    nearestFirst(routing?.margins ?? [], exitX)
  );
  const gutters = candidates.filter(gutterX => !used(gutterX)).concat(candidates.filter(used));
  const chosen = gutters.find(gutterX => clear(viaGutter(gutterX))) ?? gutters[0];
  const points = chosen === undefined ? direct : viaGutter(chosen);
  claim(points, chosen);
  return {
    ...base,
    ...geometry(points),
    anchor: { x: round(points[0].x), y: round(exitY + (points[1].y - exitY) * ANCHOR_FRACTION) },
    route,
  };
};

/**
 * True when no segment of the polyline passes through a node's rectangle,
 * ignoring the nodes named in `exceptIds` (a conductor may, and must, touch
 * its own two ends). Touching an edge is not passing through: the segments
 * are axis-aligned, so the test is a strict box overlap.
 */
export const polylineIsClear = (
  points: readonly ConductorAnchor[],
  obstacles: readonly PowerFlowNode[],
  exceptIds: readonly string[] = []
): boolean => {
  const skip = new Set(exceptIds);
  for (let index = 1; index < points.length; index += 1) {
    for (const node of obstacles) {
      if (skip.has(node.id)) continue;
      if (segmentCrossesRect(points[index - 1], points[index], node)) return false;
    }
  }
  return true;
};

/** Strict overlap between an axis-aligned segment's box and a node's rect. */
export const segmentCrossesRect = (
  from: ConductorAnchor,
  to: ConductorAnchor,
  rect: { x: number; y: number; w: number; h: number }
): boolean => {
  const left = Math.min(from.x, to.x);
  const right = Math.max(from.x, to.x);
  const top = Math.min(from.y, to.y);
  const bottom = Math.max(from.y, to.y);
  return (
    right > rect.x + EDGE_EPSILON &&
    left < rect.x + rect.w - EDGE_EPSILON &&
    bottom > rect.y + EDGE_EPSILON &&
    top < rect.y + rect.h - EDGE_EPSILON
  );
};

/** The gutter x closest to `x`, or undefined when there are none. */
export const nearestGutterX = (gutters: readonly number[], x: number): number | undefined =>
  nearestFirst(gutters, x)[0];

const nearestFirst = (gutters: readonly number[], x: number): number[] =>
  gutters.slice().sort((a, b) => Math.abs(a - x) - Math.abs(b - x) || a - b);

/** Sub-pixel slack so a run along a node's edge does not count as through it. */
const EDGE_EPSILON = 0.01;

/** `points` → `M x y L x y …`. */
export const pathOf = (points: readonly ConductorAnchor[]): string =>
  points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${round(p.x)} ${round(p.y)}`).join(' ');

/**
 * `M x y L x y …` → `points`; the inverse of `pathOf`, for **fixtures only**.
 * Production code never parses a path — a conductor carries its `points`.
 */
export const parsePath = (d: string): ConductorAnchor[] =>
  Array.from(d.matchAll(/[ML]\s*(-?[\d.]+)[\s,]+(-?[\d.]+)/g)).map(m => ({
    x: Number(m[1]),
    y: Number(m[2]),
  }));

/** A conductor attaches at the node's port: its symbol, or failing that its centre. */
const centreOf = (node: PowerFlowNode): number => portOf(node);

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

/**
 * A conductor is drawn at the heavier of its two ends' classes. The run between
 * a medium-voltage switchboard and a low-voltage UPS is medium-voltage cable up
 * to the transformer, and drawing it thin would understate it.
 */
const VOLTAGE_WEIGHT: Record<VoltageClass, number> = { mv: 3, dc: 2, lv: 1, unknown: 0 };

const heavierOf = (a: PowerFlowNode, b: PowerFlowNode): VoltageClass =>
  VOLTAGE_WEIGHT[a.vertex.voltageClass] >= VOLTAGE_WEIGHT[b.vertex.voltageClass]
    ? a.vertex.voltageClass
    : b.vertex.voltageClass;

/** Two decimals: enough for sub-pixel accuracy, short enough to keep paths small. */
const round = (value: number): number => Math.round(value * 100) / 100;
